import { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { getAccessToken } from "services/auth";
import {
  getNotificationInboxAPI,
  markNotificationReadAPI,
  markAllNotificationsReadAPI,
} from "services/api/notifications/api";
import { NotificationInboxItem } from "services/api/notifications/types";
import { showNotification } from "services/api/snackbar";
import { stripHtml } from "helpers";

const HUB_URL = `${import.meta.env.VITE_API_URL}/hubs/notifications`;

/** Preglednik bez Notification API-ja (stariji, ili nesigurni kontekst) tretira se kao odbijen. */
const readPermission = (): NotificationPermission | "unsupported" =>
  typeof Notification === "undefined" ? "unsupported" : Notification.permission;

export const useNotificationHub = (
  /** Klik na obavijest preglednika; hook ne zna za rute, pa navigaciju prepušta pozivatelju. */
  onBrowserNotificationClick?: (item: NotificationInboxItem) => void
) => {
  const [inboxItems, setInboxItems] = useState<NotificationInboxItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permission, setPermission] = useState(readPermission);
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  // Callback se drži u refu da promjena njegove reference ne ruši SignalR vezu.
  const clickHandlerRef = useRef(onBrowserNotificationClick);
  clickHandlerRef.current = onBrowserNotificationClick;

  const fetchInbox = useCallback(async () => {
    try {
      const data = await getNotificationInboxAPI();
      setInboxItems(data.items ?? []);
      setUnreadCount(data.unreadCount ?? 0);
    } catch {
      // silently fail — inbox is non-critical
    }
  }, []);

  /**
   * Obavijest preglednika — samo kad je kartica u pozadini. Dok je korisnik u aplikaciji
   * dovoljni su toast i zvono; dvostruka obavijest bi bila smetnja.
   */
  const showBrowserNotification = useCallback((item: NotificationInboxItem) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (!document.hidden) return;

    // Poruka nosi HTML iz predloška, a obavijest preglednika prikazuje čisti tekst.
    const browserNotification = new Notification(item.sentTitle, {
      body: stripHtml(item.sentMessage),
      tag: item.id,
    });

    browserNotification.onclick = () => {
      window.focus();
      browserNotification.close();
      clickHandlerRef.current?.(item);
    };
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;

    setPermission(await Notification.requestPermission());
  }, []);

  useEffect(() => {
    fetchInbox();

    if (!getAccessToken()) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        // Token se čita pri svakom pokušaju spajanja, ne jednom pri montiranju.
        //
        // SignalR ne prolazi kroz axios presretač, pa mu istekli token nitko ne osvježi: sa
        // zamrznutom vrijednošću svako ponovno spajanje pada na 401 i zvono ostaje mrtvo do
        // osvježavanja stranice.
        accessTokenFactory: () => getAccessToken() ?? "",
      })
      .withAutomaticReconnect()
      .build();

    connection.on("ReceiveNotification", (notification: any) => {
      const newItem: NotificationInboxItem = {
        id: notification.id,
        sentTitle: notification.sentTitle,
        sentMessage: notification.sentMessage,
        entityDisplayName: notification.entityDisplayName,
        entityId: notification.entityId ?? null,
        entityType: notification.entityType ?? null,
        parentEntityId: notification.parentEntityId ?? null,
        isRead: false,
        readAt: null,
        created: notification.created,
      };

      setInboxItems((prev) => [newItem, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Bez ovoga se obavijest samo tiho pribroji zvonu, pa korisnik koji ne gleda gore
      // ne sazna da je išta stiglo.
      showNotification(newItem.sentTitle, { variant: "info" });

      showBrowserNotification(newItem);
    });

    const started = connection.start().catch(() => {
      // connection failed — continue with REST-only
    });

    connectionRef.current = connection;

    return () => {
      // Gašenje čeka da pregovaranje završi. Prekid usred njega SignalR prijavljuje kao grešku, a
      // u razvoju React montira efekt dvaput, pa se javljala pri svakom otvaranju aplikacije.
      started.then(() => connection.stop());
    };
  }, [fetchInbox]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await markNotificationReadAPI(id);
      setInboxItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silently fail
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsReadAPI();
      setInboxItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  }, []);

  return {
    inboxItems,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchInbox,
    /** `default` = još nije pitano, `granted` / `denied` / `unsupported`. */
    permission,
    requestPermission,
  };
};
